import { Log } from 'bblog';

export interface IkePromiseTask {
	// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
	resolve: Function;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
	reject: Function;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
	task: Function;
	promise: Promise<any>;
}

export interface IkePromiseWorkerStats {
	id: string;
	completed: number;
	failed: number;
}

export class IkePromiseWorker {
	id: string;
	failed: number = 0;
	completed: number = 0;
	promise: Promise<void> = Promise.resolve();

	constructor(id: number) {
		this.id = 'worker-' + id;
	}

	async run(task: IkePromiseTask) {
		await this.promise;
		try {
			const result = await task.task();
			task.resolve(result);
			this.completed++;
		} catch (e) {
			task.reject(e);
			this.failed++;
		}
	}

	stats(): IkePromiseWorkerStats {
		return {
			id: this.id,
			completed: this.completed,
			failed: this.failed,
		};
	}
}
const NOOP = () => null;
/**
 * Queue for promises, will run at most "concurrency" promises at the same time.
 *
 * Example:
 *  const pq = new IkePromiseQueue(2);
 *  pq.add(() => sleep(200))
 *  await pq.join()
 *
 */
export class IkePromiseQueue {
	/** All workers used by this queue */
	workers: IkePromiseWorker[] = [];
	/* Queue of workers ready to be used */
	ready: IkePromiseWorker[] = [];

	/** Queue of tasks to be processed */
	tasks: IkePromiseTask[] = [];

	concurrency: number;

	onIdle: () => void;

	constructor(
		protected log: Log,
		concurrency: number
	) {
		this.concurrency = concurrency;

		for (let i = 0; i < this.concurrency; i++) {
			this.ready[i] = this.workers[i] = new IkePromiseWorker(i);
		}

		this.onIdle = NOOP;
	}

	/**
	 *  Join wait for the queue to empty.
	 * To make sure all the promises are successful, you must wait for the promises created from .add()
	 */
	async join() {
		if (this.isIdle) {
			return Promise.resolve();
		}
		return new Promise<void>((resolve) => {
			const existingResolve = this.onIdle;
			this.onIdle = () => {
				existingResolve();
				resolve();
			};
		});
	}

	/**
	 * Queue a task in the promise queue.
	 * If the task fails, the error will be logged.
	 */
	add<T>(task: () => Promise<T>): void {
		this.addAndReturn(task).catch((error) => {
			// Since the caller did not return this promise,
			// if it rejects then we must handle it here or the app might crash.

			this.log.error({ error }, 'PromiseQueue: caught error from task that did not have a rejection handler');
		});
	}

	/**
	 * Queue a task in the promise queue.
	 * Will resolve the returned promise when the task has been executed.
	 */
	addAndReturn<T>(task: () => Promise<T>): Promise<T> {
		const taskObject: IkePromiseTask = { task, resolve: null, reject: null, promise: null };
		// eslint-disable @typescript-eslint/no-floating-promises
		const promise = new Promise<T>((resolve, reject) => {
			taskObject.resolve = resolve;
			taskObject.reject = reject;
			this.tasks.push(taskObject);
			if (this.isAvailable) {
				this.run().catch((error) => {
					// Since this call to run is not returned or awaited,
					// if it rejects then we must handle it here or the app might crash.

					this.log.error({ error }, 'PromiseQueue: caught error while calling run() inside addAndReturn');
				});
			}
		});
		taskObject.promise = promise;
		return promise;
	}

	async run(): Promise<void> {
		if (this.isIdle) {
			this.onIdle();
			this.onIdle = NOOP;
			return null;
		}

		if (this.tasks.length === 0) {
			return null;
		}

		const task = this.tasks.shift();
		if (task == null) {
			return this.run();
		}
		const worker = this.ready.shift();

		await worker.run(task);
		this.ready.push(worker);
		this.run().catch((error) => {
			// Since this call to run is not returned or awaited,
			// if it rejects then we must handle it here or the app might crash.

			this.log.error({ error }, 'PromiseQueue: caught error while calling run() recursively inside run()');
		});
	}

	/**
	 * Are there any available workers ready
	 */
	get isAvailable() {
		return this.ready.length > 0;
	}

	/** Are there any tasks left to do */
	get isIdle() {
		return this.tasks.length === 0 && this.ready.length === this.concurrency;
	}

	/** Get the resolution stats of a worker, the number of resolved and  promises */
	stats(): { [workerId: string]: IkePromiseWorkerStats } {
		const stats = {};
		for (const worker of this.workers) {
			stats[worker.id] = worker.stats();
		}
		return stats;
	}
}
